import { TaskState, TaskConversationState, ITask } from '@nimbus-assistant/models/task.model';
import { Channel, Modality } from '@nimbus-assistant/models/task-types.model';
import { ICONS, Icon } from '@nimbus-library/features/shared/resources/icons';
import { isConsultationSessionInProgress } from '@nimbus-library/features/shared/models/participant-info/consultation-session.utils';

// --- State classification ---

const TRANSFER_DISABLED_CONVERSATION_STATES: TaskConversationState[] = [
  TaskConversationState.Parked,
  TaskConversationState.PutOnHold,
  TaskConversationState.DialingOut,
];

export function isAcwState(state: TaskState): boolean {
  return state === TaskState.InACW || state === TaskState.InExtendedACW;
}

/**
 * Outbound call with workflow: the agent leg is already up while the backend dials the target.
 * The session is connected, but there is nobody on the other side to transfer or consult yet.
 */
export function isDialingOut(task: ITask): boolean {
  return task?.conversationState === TaskConversationState.DialingOut;
}

export function isTransferDisabled(task: ITask): boolean {
  return TRANSFER_DISABLED_CONVERSATION_STATES.includes(task?.conversationState);
}

/**
 * Unify calls run over ACS and carry the `AcsCalling` channel. `channel` is nullable and only
 * Stingray stamps it, so test positively and treat null as Extend. The codes response carries
 * the channel too; fall back to the task's own channel for tasks whose codes are not loaded yet.
 */
export function isUnifyTask(task: ITask): boolean {
  return (task?.conversationCodes?.channel ?? task?.channel) === Channel.AcsCalling;
}

/**
 * Codes and tags may only be edited while the agent is actually talking to the other side.
 * Mirrors Portal My Sessions: the fields stay disabled while the backend is still dialing out
 * (outbound call with workflow) and while the conversation is parked.
 * On a Unify call they are read-only: NimbusOne owns them and a write from here would diverge.
 */
export function isCodesEditingDisabled(task: ITask): boolean {
  return (
    task?.conversationState === TaskConversationState.DialingOut ||
    task?.conversationState === TaskConversationState.Parked ||
    task?.state === TaskState.Parked ||
    isUnifyTask(task)
  );
}

// --- Modality → icon ---

export function getModalityIcon(task: ITask): Icon {
  switch (task?.modality) {
    case Modality.IM:
      return ICONS.COMMENT_ALT;
    case Modality.ExternalTask:
      return ICONS.EXTERNAL_TASK;
    case Modality.Email:
      return ICONS.ENVELOPE;
    case Modality.Audio:
    default:
      return ICONS.PHONE;
  }
}

// --- Banner priority ---
// RONA > ACW > Connected > Availability (Ringing does not change the banner)

export enum BannerPriority {
  Rona = 0,
  Acw = 1,
  Connected = 2,
  Availability = 3,
}

export function getBannerPriority(connected: ITask | null, acw: ITask | null, isRona: boolean): BannerPriority {
  if (isRona) {
    return BannerPriority.Rona;
  }
  if (acw) {
    return BannerPriority.Acw;
  }
  if (connected) {
    return BannerPriority.Connected;
  }
  return BannerPriority.Availability;
}

// --- Footer priority ---
// Connected AV > Connected non-AV > Incoming > Parked > NarRequest > none

export enum FooterPriority {
  Transfer = 0,
  OnHold = 1,
  ConnectedInfo = 2,
  IncomingInfo = 3,
  Parked = 4,
  NarRequest = 5,
  None = 6,
}

export function getFooterPriority(
  connected: ITask | null,
  incoming: ITask | null,
  parkedCount: number,
  isAudio: boolean,
  hasOpenNarRequest: boolean = false,
): FooterPriority {
  if (connected && isAudio && !connected.isDirectCall && !isDialingOut(connected)) {
    if (
      connected.conversationState !== TaskConversationState.PutOnHold ||
      isConsultationSessionInProgress(connected.consultationSession?.currentState)
    ) {
      return FooterPriority.Transfer;
    }
    return FooterPriority.OnHold;
  }
  if (connected) {
    return FooterPriority.ConnectedInfo;
  }
  if (incoming) {
    return FooterPriority.IncomingInfo;
  }
  if (parkedCount > 0) {
    return FooterPriority.Parked;
  }
  if (hasOpenNarRequest) {
    return FooterPriority.NarRequest;
  }
  return FooterPriority.None;
}

// --- Active task priority ---
// Connected > ACW > Incoming > null

export function resolveActiveTask(connected: ITask | null, acw: ITask | null, incoming: ITask | null): ITask | null {
  return connected ?? acw ?? incoming ?? null;
}
